import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import Icon from './Icon';

/* One search across B/N: posts, comments, your groups, your connections, news
   (if you have it on) and your own files — only ever what you can already see.
   The box is small and sits in the right column; results take the main column.
   Press / anywhere to jump to it. */

const SECTIONS = [
  ['posts', 'Posts'], ['comments', 'Comments'], ['groups', 'Your groups'],
  ['people', 'Your connections'], ['news', 'News'], ['files', 'Your files'],
];
const KINDS = [['', 'Any type'], ['post', 'Posts'], ['ask', 'Asks'], ['test', 'Tests'], ['offer', 'Offers'],
  ['intro', 'Intros'], ['looking_for', 'Looking for'], ['refer', 'Referrals']];
const WHEN = [[0, 'Any time'], [7, 'Past week'], [30, 'Past month'], [365, 'Past year']];

function Highlight({ text, terms }) {
  if (!text || !terms?.length) return text || null;
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  // split() with one capture group puts the matches at the odd positions.
  return text.split(re).map((part, i) => (i % 2 === 1
    ? <mark key={i}>{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>));
}

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export function FindInput({ value, onChange, className = '' }) {
  const input = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target.tagName;
      // ⌘K / Ctrl+K from anywhere, or / when you're not typing.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
        return;
      }
      if (e.key === '/' && t !== 'INPUT' && t !== 'TEXTAREA' && t !== 'SELECT' && !e.target.isContentEditable) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className={`find-box sm ${className}`}>
      <Icon name="search" size={16} />
      <input ref={input} className="find-in" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="Search" aria-label="Search Builders' Network (⌘K)"
        onKeyDown={(e) => { if (e.key === 'Escape') onChange(''); }} />
      {value ? <button className="linklike" aria-label="Clear search" onClick={() => onChange('')}>×</button>
        : <kbd className="find-kbd" aria-hidden="true">{MAC ? '⌘ K' : 'Ctrl K'}</kbd>}
    </div>
  );
}

export function FindResults({ q, onClear, onOpenPost, onGo, notify }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState('');
  const [since, setSince] = useState(0);
  const [mine, setMine] = useState(false);
  const [fromId, setFromId] = useState('');
  const [people, setPeople] = useState([]);

  useEffect(() => { api.connections().then((r) => setPeople(r.map((c) => c.other))).catch(() => {}); }, []);
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setRes(null); return undefined; }
    setBusy(true);
    const t = setTimeout(() => {
      api.find(query, { kind, sinceDays: since, mine, fromId })
        .then(setRes).catch((e) => notify?.(e.message)).finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, kind, since, mine, fromId, notify]);

  function open(type, r) {
    if (type === 'posts' || type === 'comments') onOpenPost(r.post_id);
    else if (type === 'groups') onGo('groups', { group: r.group_id, topic: r.topic_id });
    else if (type === 'people') onGo('people', { thread: r.thread_id || undefined });
    else if (type === 'news') window.open(r.url, '_blank', 'noopener,noreferrer');
    else if (type === 'files') api.downloadFile(r.id, r.title).catch((e) => notify?.(e.message));
  }

  const total = res ? Object.values(res.results).reduce((n, l) => n + l.length, 0) : 0;
  return (
    <div className="find">
      <div className="find-head">
        <h2>Results for “{q.trim()}”</h2>
        <button className="linklike" onClick={onClear}>Back to the feed</button>
      </div>
      <div className="find-filters">
        <select className="in sm" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Type">
          {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select className="in sm" value={fromId} onChange={(e) => { setFromId(e.target.value); setMine(false); }} aria-label="From">
          <option value="">From anyone</option>
          {people.map((p) => <option key={p.id} value={p.id}>From {p.name}</option>)}
        </select>
        <select className="in sm" value={since} onChange={(e) => setSince(Number(e.target.value))} aria-label="When">
          {WHEN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="muted-sm"><input type="checkbox" checked={mine} onChange={(e) => { setMine(e.target.checked); setFromId(''); }} /> Only mine</label>
      </div>
      {q.trim().length < 2 && <p className="muted-sm">Type at least two letters.</p>}
      {res && (
        <div className="find-results" aria-live="polite">
          {busy && <p className="muted-sm">Searching…</p>}
          {!busy && total === 0 && (
            <p className="muted-sm">Nothing found. Try other words — or ask Copilot: “find the post where …”</p>
          )}
          {SECTIONS.filter(([k]) => res.results[k]?.length).map(([k, label]) => (
            <section key={k} className="find-sec">
              <h3>{label}</h3>
              {res.results[k].map((r) => (
                <button key={`${k}-${r.id}`} type="button" className="find-hit" onClick={() => open(k, r)}>
                  <span className="find-hit-top">
                    <b><Highlight text={r.title} terms={res.terms} /></b>
                    <span className="muted-sm">{r.by?.name}{r.at ? ` · ${day(r.at)}` : ''}</span>
                  </span>
                  {r.snippet && <span className="find-hit-snip"><Highlight text={r.snippet} terms={res.terms} /></span>}
                </button>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
